import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { resolveStorageUrl } from '@/utils/storageUrl';

interface HeroImageResult {
  heroImage: string | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useHeroImage(eventId: string, eventType: string): HeroImageResult {
  const [heroImage, setHeroImage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadHeroImage = async () => {
    if (!eventId || !eventType) {
      console.log('useHeroImage: Missing eventId or eventType', { eventId, eventType });
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // FIRST: Check for hero block images in event_content_blocks
      // This allows images uploaded via block editor to display as hero
      const { data: heroBlocks } = await supabase
        .from('event_content_blocks')
        .select('details')
        .eq('event_id', eventId)
        .ilike('block_key', '%hero%')
        .order('display_order', { ascending: true });

      if (heroBlocks && heroBlocks.length > 0) {
        for (const block of heroBlocks) {
          const details = typeof block.details === 'string' 
            ? JSON.parse(block.details) 
            : block.details;
          
          // PRIORITY 1: Check for explicitly set background_image (from block editor "Set as Background")
          if (details?.background_image) {
            const bgImage = details.background_image;
            const imageUrl = bgImage.publicUrl || bgImage.url || (typeof bgImage === 'string' ? bgImage : null);
            if (imageUrl) {
              console.log('useHeroImage: Found background_image', imageUrl);
              setHeroImage(imageUrl);
              return;
            }
          }
          
          // PRIORITY 2: Check block_images gallery
          let blockImages = details?.block_images;
          
          // Handle nested structure: {gallery: [...]} vs flat array [...]
          if (blockImages && !Array.isArray(blockImages) && blockImages.gallery && Array.isArray(blockImages.gallery)) {
            blockImages = blockImages.gallery;
          }
          
          if (Array.isArray(blockImages) && blockImages.length > 0) {
            // Use first gallery image from block
            const firstImage = blockImages[0];
            const imageUrl = firstImage.publicUrl || firstImage.url || firstImage.image_url;
            if (imageUrl) {
              console.log('useHeroImage: Found block image', imageUrl);
              setHeroImage(imageUrl);
              return;
            }
          }
        }
      }

      // FALLBACK: Query event_images table
      const eventTypesToCheck = [eventType, `${eventType}s`];
      if (eventType === 'bootcamp') {
        eventTypesToCheck.push('festival', 'festivals');
      }
      
      console.log('useHeroImage: Searching for hero images', { 
        eventId, 
        eventType, 
        eventTypesToCheck 
      });
      
      const { data: images, error: fetchError } = await supabase
        .from('event_images')
        .select('*')
        .eq('event_id', eventId)
        .in('category', ['thumbnail', 'hero'])
        .eq('is_active', true)
        .order('is_primary', { ascending: false })
        .order('created_at', { ascending: false });

      console.log('useHeroImage: Query result', { images, fetchError });

      if (fetchError) {
        throw fetchError;
      }

      if (!images || images.length === 0) {
        setHeroImage(null);
        return;
      }

      const selectedImage = images.find(img => img.is_primary) || images[0];
      
      setHeroImage(resolveStorageUrl(selectedImage.file_path));
    } catch (err) {
      console.error('Error loading hero image:', err);
      setError(err instanceof Error ? err.message : 'Failed to load hero image');
      setHeroImage(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHeroImage();
    
    const checkForUpdates = () => {
      const updateSignal = localStorage.getItem(`heroImageUpdated_${eventId}`);
      if (updateSignal) {
        localStorage.removeItem(`heroImageUpdated_${eventId}`);
        loadHeroImage();
      }
    };
    
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkForUpdates();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    checkForUpdates();
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [eventId, eventType]);

  const refetch = () => {
    loadHeroImage();
  };

  return { heroImage, loading, error, refetch };
}